import PolicyRequired from "enums/PolicyRequired";
import { presetLimits } from "validations/spendingLimits";
import { useMutation, useQuery } from "@apollo/client";
import { isEmpty } from "lodash";
import { useFormik } from "formik";
import { useEffect } from "react";
import {
  CardPolicy,
  getCardPolicyInitialValues,
  hasAtLeastOneLimit,
} from "utils/cards/cardFormUtils";
import {
  UPDATE_CARD_PRESET,
  GET_CARD_PRESET,
  ADD_CARD_PRESET,
} from "graphql/cardPresets";

interface useCardPresetDataProps {
  presetId?: string;
}

export function useCardPresetData({ presetId }: useCardPresetDataProps) {
  const [addCardPreset, { loading: addingCardPreset }] =
    useMutation(ADD_CARD_PRESET);
  const [updateCardPreset, { loading: updatingCardPreset }] =
    useMutation(UPDATE_CARD_PRESET);

  const { data: cardPresetData, loading: loadingCardPresetData } = useQuery(
    GET_CARD_PRESET,
    {
      variables: {
        id: presetId,
      },
      fetchPolicy: "network-only",
      skip: isEmpty(presetId),
    },
  );

  const formikCardPreset = useFormik<CardPolicy>({
    initialValues: getCardPolicyInitialValues(),
    validationSchema: presetLimits,
    onSubmit: async () => {},
  });

  useEffect(() => {
    const preset = cardPresetData?.cardPreset;

    if (preset?.id) {
      const formikCardPresetValues = {
        nameOfPreset: preset.presetName || "",
        transactionLimit: preset.transactionLimit || "",
        dailyLimit: preset.dailyLimit || "",
        monthlyLimit: preset.monthlyLimit || "",
        weeklyLimit: preset.weeklyLimit || "",
        totalLimit: preset.totalLimit || "",
        cancelationTimeFrame: {
          timeFrameValue: preset?.cancellationTimeFrame?.timeFrameValue || null,
          timeFrameFrequency:
            preset?.cancellationTimeFrame?.timeFrameFrequency || null,
        },
        expenseCategory: {
          id: preset.expenseCategoryData?.id || "",
          name: preset.expenseCategoryData?.name || "",
          level: preset.expenseCategoryData?.level || 0,
        },
        allocationType: {
          key: preset.allocationData?.key || "",
          name: preset.allocationData?.name || "",
        },
        allocationCode: {
          id: preset.allocationData?.id || "",
          allocationCode: preset.allocationData?.allocationCode || "",
        },
        receiptPolicy: preset.receiptPolicy || PolicyRequired.policy,
        allocationPolicy: preset.allocationPolicy || PolicyRequired.policy,
        allowedMerchants: preset.allowedMerchants || [],
        allowedCategories: preset.allowedCategories || [],
        blockedMerchants: preset.blockedMerchants || [],
        blockedCategories: preset.blockedCategories || [],
      };

      formikCardPreset.resetForm({ values: formikCardPresetValues });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardPresetData]);

  const handleSubmitCardPreset = async () => {
    const presetValues = formikCardPreset.values;

    const input = {
      presetName: presetValues.nameOfPreset || "",
      transactionLimit: parseFloat(presetValues.transactionLimit) || null,
      dailyLimit: parseFloat(presetValues.dailyLimit) || null,
      weeklyLimit: parseFloat(presetValues.weeklyLimit) || null,
      monthlyLimit: parseFloat(presetValues.monthlyLimit) || null,
      totalLimit: parseFloat(presetValues.totalLimit) || null,
      expenseCategory: presetValues.expenseCategory?.id || "",
      allocationId: presetValues.allocationCode?.id || "",
      receiptPolicy: presetValues.receiptPolicy || PolicyRequired.policy,
      allocationPolicy: presetValues.allocationPolicy || PolicyRequired.policy,
      cancellationTimeFrame: {
        ...presetValues.cancelationTimeFrame,
        timeFrameValue:
          presetValues.cancelationTimeFrame?.timeFrameValue !== null &&
          presetValues.cancelationTimeFrame?.timeFrameValue !== undefined
            ? parseInt(
                presetValues.cancelationTimeFrame?.timeFrameValue.toString(),
                10,
              )
            : null,
      },
      allowedMerchants: presetValues.allowedMerchants?.map(
        ({ id, name }: any) => ({ id, name }),
      ),
      allowedCategories: presetValues.allowedCategories?.map(
        ({ id, name }: any) => ({ id, name }),
      ),
      blockedMerchants: presetValues.blockedMerchants?.map(
        ({ id, name }: any) => ({ id, name }),
      ),
      blockedCategories: presetValues.blockedCategories?.map(
        ({ id, name }: any) => ({ id, name }),
      ),
    };
    let result;

    if (presetId) {
      result = await updateCardPreset({
        variables: {
          id: presetId,
          input: input,
        },
      });
    } else {
      result = await addCardPreset({
        variables: {
          input: input,
        },
      });
    }

    return result;
  };

  const atLeastOneLimit = hasAtLeastOneLimit(formikCardPreset.values);

  const specificLoadingStates = {
    loadingCardPresetData,
    addingCardPreset,
    updatingCardPreset,
  };

  const isAnyLoading = Object.values(specificLoadingStates).some(
    (loading) => loading,
  );

  return {
    formikCardPreset,
    atLeastOneLimit,
    specificLoadingStates,
    isAnyLoading,
    handleSubmitCardPreset,
  };
}
